import type { ApolloCache, ApolloError, FetchResult } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import type { MutationResult } from '../../graphql';
import type { Dbx } from '../account-store';
import type { ContactRole } from '../dbx';
import { QUERY as QUERYMAINACCOUNT } from '../use-account';
import { QUERY as QUERYSUBUSERS } from '../use-sub-users-account';

interface TVariables {
  contact_role: string;
  client_id: number;
  input: inputsArgs[];
}

interface TVResult {
  upsertContactRole: ContactRole[];
}

interface Args {
  contactRole: string;
  input: inputsArgs[];
  accountId: number;
}

interface inputsArgs {
  user_id: number;
  location_id?: string;
  value: boolean;
  priority?: number;
}

interface TDataGetUsers {
  getSubUsersForAccount: {
    result: Dbx.SubUserAccountsRecord[];
  };
}

interface TDataGetAccount {
  getAccount?: Dbx.Record;
}

interface TVariablesGetUsers {
  id: number;
}

const MUTATION = gql`
  mutation upsertContactRole($input: [UpsertContactRoleInput!]!, $contact_role: String!, $client_id: Float) {
    upsertContactRole(input: $input, contact_role: $contact_role, client_id: $client_id) {
      id
      onboarding_main
      onboarding_technical
      onsite_technical
      onsite_technical_priority
      primary_billing
      shipping
      user {
        id
        email
      }
      location {
        id
        name
        is_disabled
      }
    }
  }
`;

export const useEditContactRole = (): [
  (args: Args) => Promise<ContactRole[] | undefined>,
  MutationResult<ContactRole[], 'contactRole'>
] => {
  const [mutation, result] = useMutation<TVResult, TVariables>(MUTATION);

  const [error, setError] = useState<Error | undefined>();
  const [contactRole, setContactRole] = useState<ContactRole[] | undefined>();

  return [
    useCallback(
      async (args: Args): Promise<ContactRole[] | undefined> => {
        let contactRole_: ContactRole[] | undefined;

        try {
          // Parse the input arguments

          const variables = {
            contact_role: args.contactRole,
            client_id: args.accountId,
            input: args.input
          };

          const response = await mutation({
            variables,
            update(cache: ApolloCache<unknown>, { data: fetchData }: FetchResult<TVResult>): void {
              const dataReadQuerySubusers = cache.readQuery<TDataGetUsers, TVariablesGetUsers>({
                query: QUERYSUBUSERS,
                variables: {
                  id: args.accountId
                }
              });

              const dataReadQueryMainAccount = cache.readQuery<TDataGetAccount, TVariablesGetUsers>(
                {
                  query: QUERYMAINACCOUNT,
                  variables: {
                    id: args.accountId
                  }
                }
              );

              const newAccountContactRole = dataReadQueryMainAccount?.getAccount?.contactRoles?.map(
                (mainContact) => {

                  const fetchContactRoleFound = fetchData?.upsertContactRole.find(
                    (fetchConactRole) => Number(fetchConactRole.user.id) === Number(mainContact?.id)
                  );

                  if(fetchContactRoleFound && fetchContactRoleFound.user.id === dataReadQueryMainAccount.getAccount?.id) {
                    if (fetchContactRoleFound.id === mainContact?.id) {
                      // evalua si el id ya existe y solo es editarlo, si no...
                      return fetchContactRoleFound;
                    }

                    if (
                      !dataReadQueryMainAccount.getAccount.contactRoles?.some(
                        (_contact) =>
                          _contact?.id &&
                          fetchData?.upsertContactRole.some(
                            (_fetchContact) => _fetchContact.id === _contact.id
                          )
                      )
                    ) {
                      // si no, evalua si existe en todos los contacts roles existenten en el Main Account, y si no existe lo debe agregar a los contact roles

                      return (
                        dataReadQueryMainAccount.getAccount.contactRoles && [
                          ...dataReadQueryMainAccount.getAccount.contactRoles,
                          fetchContactRoleFound
                        ]
                      );
                    }
                  }

                  return mainContact;
                }
              );

              const newAccountData = {
                ...dataReadQueryMainAccount?.getAccount,
                contactRoles: newAccountContactRole
              };

              const newSubusersData = dataReadQuerySubusers?.getSubUsersForAccount.result.map(
                (subuser) => {
                  const fetchContactRoleFound = fetchData?.upsertContactRole.find(
                    (fetchConactRole) => fetchConactRole.user.id === subuser.id
                  );

                  if (fetchContactRoleFound) {
                    // el contact role a evaluar es de este subuser

                    const newSubuserContactRole = subuser.contactRoles?.map(
                      (subuserContactRole) => {
                        if (subuserContactRole?.id === fetchContactRoleFound.id) {
                          // evalua si el contact role editado está en en el usuario para agregar

                          return fetchContactRoleFound;
                        }

                        return subuserContactRole;
                      }
                    );

                    if (
                      subuser.contactRoles?.some(
                        (_contact) =>
                          _contact?.id &&
                          fetchData?.upsertContactRole.some(
                            (_fetchContact) => _fetchContact.id === _contact.id
                          )
                      )
                    ) {
                      // Quiero la negación por que la negación es que no encontró id similiares
                      return { ...subuser, contactRoles: newSubuserContactRole };
                    }

                    const newSubuser = newSubuserContactRole && {
                      ...subuser,
                      contactRoles: [...newSubuserContactRole, fetchContactRoleFound]
                    };

                    return newSubuser;
                  }

                  return subuser;
                }
              );

              const reWriteQuerySubuser = {
                getSubUsersForAccount: {
                  ...dataReadQuerySubusers?.getSubUsersForAccount,
                  result: newSubusersData
                }
              };

              cache.writeQuery({
                query: QUERYSUBUSERS,
                data: reWriteQuerySubuser
              });

              const reWriteQueryMain = {
                ...dataReadQueryMainAccount,
                getAccount: {
                  newAccountData
                }
              };

              cache.writeQuery({
                query: QUERYMAINACCOUNT,
                data: reWriteQueryMain
              });
            }
          })
            .then(({ data }: FetchResult<TVResult>) => data)
            .catch((_error: ApolloError) => {
              throw new Error('EventID/ClientID is required.');
            });

          // Parse the server response

          contactRole_ = response ? response.upsertContactRole : undefined;

          setContactRole(contactRole_);

          if (!contactRole_) {
            throw new Error('EventID/ClientID is required.');
          }
        } catch (error_: unknown) {
          setError(<Error>error_);
        }

        return contactRole_;
      },
      [mutation, setError, setContactRole]
    ),
    {
      contactRole,
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
